import { ensureCognitoUser, requestMagicLink } from "@src/appV2/Auth/cognito/cognitoAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logAuthRedirectFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";

interface SendLoginEmailParams {
  email: string;
  requestedURL?: string | undefined;
}

export type SendLoginEmailResult = { success: true } | { success: false; error: string };

/**
 * Sends a Cognito magic link login email.
 *
 * Calls `ensureCognitoUser` first so legacy workplace users (who exist in
 * `facilityusers` but not yet in the Cognito user pool) are JIT-imported
 * before the CUSTOM_AUTH flow runs. CUSTOM_AUTH does not invoke the
 * PreAuthentication Lambda, so the import has to be triggered from the
 * client — without this, pre-existing users hit "No registered user found"
 * on first login attempt in the redesigned app.
 */
export async function sendLoginEmail({
  email,
}: SendLoginEmailParams): Promise<SendLoginEmailResult> {
  setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_MAGIC_LINK);

  try {
    await ensureCognitoUser(email);
    await requestMagicLink(email);
    return { success: true };
  } catch (error: unknown) {
    const mappedError = mapCognitoError(error, "magic-link-request");
    const { outcome, reason } =
      mappedError.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mappedError.code);
    logAuthRedirectFailure({
      error,
      metadata: {
        errorCode: mappedError.code,
        errorMessage: mappedError.message,
        email,
        outcome,
        reason,
      },
    });
    return { success: false, error: mappedError.message };
  }
}
